import { useCallback, useEffect, useRef, useState } from 'react';
import { Box } from '@gemini/core';
import { FeedbackMessage } from '@gemini/ui';
import { TokenError } from '@mfe-presentation/constants/updatePage';
import { SmsServiceCode, ResultStatus } from '@mfe-presentation/constants/userVerification';
import { useAppContext } from '@mfe-presentation/contexts';
import { LocationError } from '@mfe-types/geo';
import { useTranslation } from '@aviv-unified-frontend/runtime/i18n';
import { NAME_SPACE } from '@mfe-presentation/hooks/useLoadTranslations';

export const toastDuration = 4000;
export const fadeoutDuration = 200;

const ToastMessage = () => {
    const { toastMessage } = useAppContext();
    const { t } = useTranslation(NAME_SPACE);
    // TODO [WLEF-477] Add dynamic way to translate errors
    const [isVisible, setIsVisible] = useState(false);
    const [isFadingOut, setIsFadingOut] = useState(false);
    const durationTimeout = useRef<NodeJS.Timeout>();
    const easeInTimeout = useRef<NodeJS.Timeout>();
    const toastRef = useRef<HTMLDivElement>(null);

    const statusMessage: Record<string, string> = {
        [ResultStatus.Blacklisted]: t(
            'common.popover.error.external.verification.blacklisted.message',
        ),
        [ResultStatus.UnsupportedCountryCode]: t(
            'common.fields.phoneNumber.error.unsupportedCountryCode',
        ),
        [ResultStatus.NotAMobilePhoneNumber]: t(
            'common.popover.warning.external.verification.notAMobilePhoneNumber.message',
        ),
        [ResultStatus.UserOptedOut]: t(
            'common.popover.error.external.verification.userOptedOut.message',
        ),
        [SmsServiceCode.VerificationRetryCountExceeded]: t(
            'common.popover.error.external.verification.verificationRetryCountExceeded.message',
        ),
        [SmsServiceCode.CodeMismatch]: t(
            'common.popover.error.external.verification.codeMismatch.message',
        ),
        [SmsServiceCode.ExpiredCode]: t(
            'common.popover.error.external.verification.expiredCode.message',
        ),
        [SmsServiceCode.VerificationCodeSent]: t(
            'common.popover.success.external.verification.phoneNumberUpdated.message',
            { replace: toastMessage?.params },
        ),
        [SmsServiceCode.VerificationCodeServerError]: t(
            'common.popover.error.external.verification.serverError.message',
        ),
        [LocationError.CityNameNotFound]: t('common.popover.error.cityName.notFound.message'),
        [TokenError.UpdatePageTokenError]: t('common.popover.error.updateEstimation.message'),
    };

    const toastContent = () => {
        if (!toastMessage) {
            return { message: '', showRefreshButton: false };
        }

        const message =
            statusMessage[toastMessage.status] ??
            t('common.popover.error.wlef.serverError.message');
        const showRefreshButton = toastMessage.status === ResultStatus.UnexpectedError;

        return { message, showRefreshButton };
    };

    const { message, showRefreshButton } = toastContent();

    const handleOnClose = useCallback(() => {
        clearTimeout(durationTimeout.current);
        clearTimeout(easeInTimeout.current);

        setIsVisible(false);
        setIsFadingOut(true);

        easeInTimeout.current = setTimeout(() => {
            setIsFadingOut(false);
        }, fadeoutDuration);
    }, [durationTimeout, easeInTimeout]);

    const handleOutsideClick = useCallback((event: MouseEvent) => {
        if (toastRef.current && !toastRef.current.contains(event.target as Node)) {
            handleOnClose();
        }
    }, []);

    useEffect(() => {
        if (toastMessage) {
            setIsVisible(true);
            clearTimeout(durationTimeout.current);
            clearTimeout(easeInTimeout.current);

            if (!toastMessage?.persistent) {
                durationTimeout.current = setTimeout(() => {
                    setIsVisible(false);
                    setIsFadingOut(true);
                }, toastDuration);

                easeInTimeout.current = setTimeout(() => {
                    setIsFadingOut(false);
                }, toastDuration + fadeoutDuration);
            }
        } else {
            handleOnClose();
        }
        document.addEventListener('click', handleOutsideClick);

        return () => {
            clearTimeout(durationTimeout.current);
            clearTimeout(easeInTimeout.current);
            document.removeEventListener('click', handleOutsideClick);
        };
    }, [toastMessage, handleOnClose, handleOutsideClick]);

    return (
        toastMessage && (
            <Box
                ref={toastRef}
                testId="toast-message"
                position="fixed"
                zIndex="zIndex.4"
                top="spacing.64"
                left="50%"
                transform="translateX(-50%)"
                margin="0 auto"
                maxWidth="600px"
                width="calc(100% - 32px)"
                transitionDuration={isVisible ? 'motionDuration.250' : 'motionDuration.200'}
                transitionTimingFunction={
                    isVisible ? 'motionEasing.ease-out' : 'motionEasing.ease-in'
                }
                opacity={isVisible ? 'opacity.full' : 'opacity.none'}
            >
                {(isVisible || isFadingOut) && (
                    <FeedbackMessage
                        testId={`toast-message-${toastMessage.status}`}
                        title={message ? '' : t('common.popover.error.wlef.serverError.title')}
                        variant={toastMessage?.type ?? 'error'}
                        startButtonProps={
                            showRefreshButton
                                ? {
                                      children: t('common.popover.cta.refresh'),
                                      onPress: () => {
                                          // reset the entire application from a clean state
                                          // it will ensures that there are no duplicate script or resource loads, but will lead to data loss
                                          window.location.reload();
                                      },
                                  }
                                : undefined
                        }
                        endButtonProps={undefined}
                        onClose={handleOnClose}
                    >
                        <Box whiteSpace="pre-line">{message}</Box>
                    </FeedbackMessage>
                )}
            </Box>
        )
    );
};

export default ToastMessage;
